/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Message Form With Agent Inner Container
 */

import { TOUR_BOOKING_SUBMIT_CLICK } from '@zcode-ui/web-zumper-integrations'
import type { SerializableDate, Unit, User } from '@zcode/bizos'
import { type AnalyticsC, type Origin, localStorageSingleton } from '@zcode/nr-utils'
import { selectIsMultifamily } from '@zcode/nr/es/shared/bizos/multifamily/selectors'
import * as MessageC from '@zcode/nr/es/shared/constants/message'
import type { selectSimilarListables } from '@zcode/nr/es/shared/modules/similarListings/selectors'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import type {
  Filters,
  FloorplanListing,
  InitialMessageData,
  Listable,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import { isDate as _isDate, format, isValid, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import type { FormikHelpers } from 'formik'
import React, { useCallback, useMemo, useState } from 'react'
import { sendMessage } from '../../../actions/sendMessage/sendMessage.action'
import { sendMessageSuccess } from '../../../actions/sendMessage/sendMessageSuccess'
import { getSavedCustomMessage } from '../../../features/SavedCustomMessage/module/thunks/getSavedCustomMessage'
import { setSavedCustomMessage } from '../../../features/SavedCustomMessage/module/thunks/setSavedCustomMessage'
import type { selectSelectedTourType } from '../../../modules/multifamilyTourTimePicker/selectors/index'
import { createMultifamilyTourRequest } from '../../../modules/multifamilyTourTimePicker/thunks/createMultifamilyTourRequest'
import { createSearch } from '../../../modules/savedSearchesView/thunks/createSearch/createSearch'
import { trackTourBookingEvent } from '../../../modules/traktor/thunks/trackTourBookingEvent'
import { useDispatch } from '../../../modules/useDispatch'
import { getFloorplanIdFromOptionValue } from './FormInputs/FloorplanInput'
import { NewMessageForm } from './NewMessageForm/NewMessageForm'
import { DEFAULT_TZ } from './TourAvailabilityPicker/helpers'
import { useTourTimeOptions } from './TourAvailabilityPicker/hooks/useTourTimeOptions'
import { getHasInstantTourAvailability } from './TourOptions/getHasInstantTourAvailability'
import type { Values } from './helpers'
import { getInitialFormValues } from './helpers/getInitialFormValues'
import { getMessageWithIntent } from './helpers/getMessageWithIntent'
import { getShouldLimitLeads } from './helpers/getShouldLimitLeads'
import { type SelectedDateAndTime, getValidationSchema } from './helpers/getValidationSchema'

// @ts-expect-error
const isDate: (anything: unknown) => anything is Date = _isDate
export const CLOSE_MOVE_IN_THRESHOLD = 14

function formatSelectedTime(
  selectedDateTime: SelectedDateAndTime | null,
  ignoreTourTime: boolean,
  tz: string
) {
  if (!selectedDateTime) {
    return null
  }
  const { selectedTime } = selectedDateTime
  const formatWithTime = "EEEE, LLL d 'at' h:mm aaa"
  const formatWithoutTime = 'EEEE, LLL d'
  return formatInTimeZone(
    selectedTime.getTime(),
    tz,
    ignoreTourTime ? formatWithoutTime : formatWithTime
  )
}

function formatRequestedTime(
  selectedDateTime: SelectedDateAndTime | null,
  ignoreTourTime: boolean,
  tz: string
) {
  if (!selectedDateTime) {
    return null
  }
  const { selectedTime } = selectedDateTime
  const formatWithTime = "yyyy-MM-dd'T'kk:mm':00.000"
  const formatWithoutTime = 'yyyy-MM-dd'
  return formatInTimeZone(
    selectedTime.getTime(),
    tz,
    ignoreTourTime ? formatWithoutTime : formatWithTime
  )
}

interface BaseMessageFormInnerContainerProps<
  Entity extends Listable | Property,
  _Origin extends Origin,
> {
  alwaysShowForm?: boolean
  allowMFTourButton?: boolean
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.HOMEPAGE
  containerClass?: string
  ctaName?: string
  currentTab?: string
  entity: Entity
  equalCTAIntent?:
    | typeof MessageC.APPLY
    | typeof MessageC.MESSAGE
    | typeof MessageC.TOUR
    | typeof MessageC.WAITLIST
  feedName?: string | null
  filters: Filters
  filtersBedroom: readonly boolean[]
  formType: typeof MessageC.MESSAGE_FORM_MODAL | typeof MessageC.MESSAGE_FORM_INLINE
  hasContext?: boolean
  hasBoxShadow?: boolean
  headerText?: string | null
  hideAllHeadings?: boolean
  hideCancelButton?: boolean
  hideContactButtons?: boolean
  hideCustomMessage?: boolean
  hideFloorplans?: boolean
  hideMoveInDate?: boolean
  ignoreEqualCTAOrigin?: boolean
  isFilterAd?: boolean | null | undefined
  isGalleryLanding?: boolean
  isLoggedIn: boolean
  isMobile: boolean
  isModal?: boolean
  isQuestionModal?: boolean
  isSendingMessage: boolean
  longTermLeadCount: number
  messageSentTime: number | null
  now: number
  onlyShowFloorplanGroup?: boolean
  onMessageMouseEnter?: React.MouseEventHandler<HTMLButtonElement>
  onPaidPhoneClick: () => void
  onSubmitError: () => void
  onView?: (args: { entity: Entity; origin: _Origin }) => void
  origin: _Origin
  selectedFloorplan?: FloorplanListing<NormalFloorplanListingData> | undefined
  selectedTime: SerializableDate | null
  selectedUnit?: Unit | null | undefined
  shortTermLeadCount: number
  showAgent?: boolean
  showCallBtnOnSlide?: boolean
  showSummary?: boolean
  similarListings: ReturnType<typeof selectSimilarListables>
  submittingTourRequest: boolean
  tourType: ReturnType<typeof selectSelectedTourType>
  unavailableMsgClass?: string
  user: User
  viewingId?: number
  viewingTime?: string
}

export type MessageFormInnerContainerProps<
  Entity extends Listable | Property,
  _Origin extends Origin,
> = BaseMessageFormInnerContainerProps<Entity, _Origin> &
  (
    | {
        hideCancelButton: true
        onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void
      }
    | {
        showAgent?: false
        onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void
      }
    | {
        onCancel: (e: React.MouseEvent<HTMLButtonElement>) => void
      }
  )

export const MessageFormInnerContainer = <
  Entity extends Listable | Property,
  _Origin extends Origin,
>({
  alwaysShowForm,
  analyticChannel,
  currentTab,
  entity,
  equalCTAIntent,
  filters,
  filtersBedroom,
  formType,
  ignoreEqualCTAOrigin,
  isFilterAd,
  isLoggedIn,
  isMobile,
  isModal,
  longTermLeadCount,
  ctaName,
  messageSentTime,
  now,
  origin,
  onSubmitError,
  selectedFloorplan,
  selectedTime,
  selectedUnit,
  shortTermLeadCount,
  tourType,
  user,
  viewingId,
  viewingTime,
  ...forwardProps
}: MessageFormInnerContainerProps<Entity, _Origin>) => {
  const dispatch = useDispatch()
  const [floorplanUserSelected, setFloorplanUserSelected] = useState(false)
  const [floorplanUserSelectedId, setFloorplanUserSelectedId] = useState(
    selectedFloorplan?.listingId
  )

  const [equalCTAIntentSelected, setEqualCTAIntentSelected] = useState(equalCTAIntent)

  const [sentMessageOnSession, setSentMessageOnSession] = useState(false)
  const [resending, setResending] = useState(false)
  const [showMessageIndicator, setShowMessageIndicator] = useState(false)

  const {
    buildingId,
    isBuilding,
    isIncomeRestricted,
    isListing,
    isMoveInDateRequired,
    isPhoneRequired,
    isRentpath,
    listingId,
  } = entity
  const tz = entity.tz ?? DEFAULT_TZ

  const isMultifamily = selectIsMultifamily(entity)
  const phoneNumberRequired = isMultifamily || isPhoneRequired || isListing
  const floorplanRequired = isMultifamily && isBuilding
  const showTourSuccessButterBar = equalCTAIntentSelected === MessageC.TOUR && !!selectedTime
  const skipFetchingTourAvailability = equalCTAIntentSelected !== MessageC.TOUR
  const { fetching: fetchingTourAvailability, tourTimeOptionsByDate } = useTourTimeOptions(
    entity,
    tourType,
    skipFetchingTourAvailability
  )

  const shouldLimitLeads = getShouldLimitLeads({ longTermLeadCount, shortTermLeadCount })

  // TODO: move this out to a separate file. worth it though? need to pass down a lot of props
  const onSubmit = useCallback(
    async (values: Values, actions: FormikHelpers<Values>) => {
      const {
        email,
        messageModified,
        moveIn,
        msg,
        name,
        phone,
        saveSearch,
        selectedFloorplanId,
        selectedDateTime,
      } = values
      const date = !moveIn ? null : isDate(moveIn) ? moveIn : parseISO(moveIn)
      const parsedMoveIn = isValid(date) ? format(date as Date, 'MM/dd/yyyy') : null
      const intent = equalCTAIntentSelected
      const selectedTime =
        selectedDateTime &&
        formatInTimeZone(selectedDateTime.selectedTime.getTime(), tz, "EEEE, LLL d 'at' h:mm aaa")
      const requestedTime =
        selectedDateTime &&
        formatInTimeZone(selectedDateTime.selectedTime.getTime(), tz, "yyyy-MM-dd'T'kk:mm':00.000")
      const floorplan = entity?.floorplans?.find(
        (fp) => fp?.listingId === Number(selectedFloorplanId)
      )
      const { bedsBathsText } = floorplan || {}

      dispatch(setSavedCustomMessage(msg))

      const message = getMessageWithIntent({
        bedsBathsText,
        intent,
        moveIn: parsedMoveIn,
        msg,
      })

      const messageData: InitialMessageData & { requestedTime: typeof requestedTime } = {
        email,
        messageModified,
        msg: message,
        moveIn: parsedMoveIn,
        name,
        origin,
        phone,
        requestedTime, // TODO: unused and doesn't need to be here?
        selectedTimes: selectedTime ? [selectedTime] : undefined,
        viewingId,
        ignoreEqualCTAOrigin,
      }

      actions.setSubmitting(false)

      if (isIncomeRestricted) {
        localStorageSingleton.set('messagedIncomeRestricted', true)
      }

      setShowMessageIndicator(true)
      // sending message
      // TODO: refactor this code w/ saga, https://zumper.atlassian.net/browse/ZUM-27611
      try {
        const result = await dispatch(
          sendMessage({
            analyticChannel,
            dlcEnabled: saveSearch,
            entity,
            equalCTAIntent: intent,
            floorplan,
            formType,
            messageData,
            moveInText: values.moveInText,
            selectedFloorplanId,
            shouldLimitLeads,
            showTourSuccessButterBar,
          })
        )
        setShowMessageIndicator(false)
        // using sentMessageOnSession to determine the appropriate messaged state
        !isModal && setSentMessageOnSession(!sentMessageOnSession)
        if (resending && !isModal) {
          setResending(false)
        }
        dispatch(
          sendMessageSuccess({
            analyticChannel,
            currentTab,
            email,
            entity,
            isFilterAd,
            ctaName,
            messageModified,
            moveIn: parsedMoveIn,
            name,
            origin,
            phone,
            result,
            saveSearch,
          })
        )
      } catch {
        setShowMessageIndicator(false)
      }

      // saveSearch
      if (values.saveSearch && !isRentpath) {
        let searchEntity
        let floorplan
        if (isBuilding) {
          const isSingleFloorplanSelected =
            !forwardProps.onlyShowFloorplanGroup &&
            selectedFloorplan &&
            selectedFloorplan.listingId === selectedFloorplanId
          if (isSingleFloorplanSelected) {
            searchEntity = selectedFloorplan
          } else {
            floorplan = floorplanUserSelected
              ? entity?.floorplans?.find((fp) => fp.listingId === selectedFloorplanId)
              : null
            searchEntity = entity
          }
        } else {
          searchEntity = entity
        }
        try {
          await dispatch(
            createSearch({
              email,
              entity: searchEntity,
              filters,
              floorplan,
              fromMessage: true,
              isLoggedIn,
              noButter: true,
              noConvert: true,
              origin,
              preventLoginPrompt: true,
            })
          )
        } finally {
          // success or Failure; failure could be for a number of reasons but we'll disable the
          // checkbox for now
          localStorageSingleton.set('preventCreateAlertFromMsg', true)
        }
      }
    },
    [
      analyticChannel,
      ctaName,
      dispatch,
      entity,
      equalCTAIntentSelected,
      filters,
      floorplanUserSelected,
      formType,
      forwardProps.onlyShowFloorplanGroup,
      selectedFloorplan,
      ignoreEqualCTAOrigin,
      isBuilding,
      isFilterAd,
      isIncomeRestricted,
      isLoggedIn,
      isModal,
      origin,
      resending,
      selectedFloorplan,
      sentMessageOnSession,
      shouldLimitLeads,
      showTourSuccessButterBar,
      tz,
      viewingId,
    ]
  )

  const onSubmitMFTourRequest = useCallback(
    async (values: Values) => {
      const {
        email,
        messageModified,
        selectedFloorplanId,
        msg,
        moveIn,
        name: fullName,
        phone,
        selectedDateTime,
        saveSearch,
        unitId,
      } = values

      const hasInstantTourAvailability = await dispatch(getHasInstantTourAvailability(entity))
      const ignoreTourTime = !hasInstantTourAvailability
      const intent = equalCTAIntentSelected
      const selectedTime = formatSelectedTime(selectedDateTime, ignoreTourTime, tz)
      const requestedTime = formatRequestedTime(selectedDateTime, ignoreTourTime, tz)

      const date = !moveIn ? null : isDate(moveIn) ? moveIn : parseISO(moveIn)
      const parsedMoveIn = isValid(date) ? format(date as Date, 'MM/dd/yyyy') : null

      dispatch(setSavedCustomMessage(msg))

      const message = getMessageWithIntent({
        intent: MessageC.TOUR,
        moveIn: parsedMoveIn,
        msg,
      })

      const formValues = {
        email,
        floorplanId: selectedFloorplanId,
        message,
        messageModified,
        moveInDate: parsedMoveIn,
        name: fullName,
        phone,
        saveSearch,
        tourType,
        unitId,
      }

      void dispatch(
        createMultifamilyTourRequest({
          analyticChannel,
          buildingId,
          dlcEnabled: saveSearch,
          equalCTAIntent: intent,
          formType,
          entity,
          formValues,
          listingId,
          origin,
          requestedTimes: requestedTime ? [requestedTime] : [],
          selectedTimes: selectedTime ? [selectedTime] : [],
          selectedFloorplanId,
          showTourSuccessButterBar,
          ignoreEqualCTAOrigin,
          noButter: true,
        })
      )
      dispatch(
        trackTourBookingEvent({
          currentTab,
          entity,
          name: TOUR_BOOKING_SUBMIT_CLICK,
          origin,
          pathname: dispatch(getRouteProps()).location.pathname,
          values: formValues,
        })
      )
      !isModal && setSentMessageOnSession(!sentMessageOnSession)
    },
    [
      analyticChannel,
      buildingId,
      currentTab,
      dispatch,
      entity,
      equalCTAIntentSelected,
      formType,
      ignoreEqualCTAOrigin,
      isModal,
      listingId,
      origin,
      sentMessageOnSession,
      showTourSuccessButterBar,
      tourType,
      tz,
    ]
  )

  selectedFloorplan = useMemo(() => {
    return floorplanUserSelected
      ? entity?.floorplans?.find((fp) => fp?.listingId === Number(floorplanUserSelectedId)) ||
          selectedFloorplan
      : selectedFloorplan
  }, [entity.floorplans, floorplanUserSelected, floorplanUserSelectedId, selectedFloorplan])

  const initialFormValues = useMemo((): Values => {
    const initialValues = getInitialFormValues({
      entity,
      filtersBedroom,
      intent: equalCTAIntentSelected,
      now,
      selectedFloorplan: floorplanUserSelected
        ? entity?.floorplans?.find((fp) => fp?.listingId === Number(floorplanUserSelectedId))
        : selectedFloorplan,
      selectedTime,
      selectedUnit,
      user,
    })

    const savedCustomMessage = dispatch(getSavedCustomMessage())

    return {
      ...initialValues,
      msg: savedCustomMessage ?? initialValues.msg,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, now, user, equalCTAIntentSelected])

  const hideForm = !!messageSentTime && !alwaysShowForm && !resending

  const allProps = {
    analyticChannel,
    entity,
    equalCTAIntent: equalCTAIntentSelected,
    formType,
    hasSelectedTime: selectedTime != null,
    hideForm,
    initialValues: initialFormValues,
    isMobile,
    isModal,
    messageSentTime,
    onEnableResending: () => setResending(true),
    onSetFloorplanUserSelected: ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
      const optionText = value.toLowerCase()
      const floorplanId = getFloorplanIdFromOptionValue({
        floorplans: entity.floorplans,
        optionText,
      })

      const floorplan = entity?.floorplans?.find((fp) => fp.listingId === floorplanId)
      // If the new floorplan is waitlistEnabled, show waitlist CTA. (Only if it's not the new tour form)
      // if not, check if the originally selected CTA was waitlist. If it was, show 'Check Availability' CTA intent
      // otherwise, show originally selected CTA Intent
      const isNewTourForm = equalCTAIntent === MessageC.TOUR
      const CTAIntent =
        floorplan?.waitlistEnabled && !isNewTourForm
          ? 'Waitlist'
          : equalCTAIntent === 'Waitlist'
            ? 'Message'
            : equalCTAIntent
      setEqualCTAIntentSelected(CTAIntent)
      setFloorplanUserSelectedId(floorplanId)
      setFloorplanUserSelected(true)
    },
    onSubmit,
    onSubmitError: () => {
      onSubmitError()
      const firstError = document.querySelectorAll<HTMLButtonElement>('img[alt~="Error:"]')?.[0]
      if (firstError) {
        firstError.focus()
        firstError.scrollIntoView({ block: 'center' })
      }
    },
    onSubmitMFTourRequest,
    origin,
    phoneNumberRequired,
    resending,
    selectedFloorplan,
    setResending,
    showMessageIndicator,
    validationSchema: getValidationSchema({
      equalCTAIntent: equalCTAIntentSelected,
      fetchingTourAvailability,
      floorplanRequired,
      moveInRequired: isMoveInDateRequired,
      phoneNumberRequired,
      tourTimeOptionsByDate,
    }),
    // state
    sentMessageOnSession,
    ...forwardProps,
  }
  return <NewMessageForm {...allProps} />
}
